/**
 * La variation d'ORDONNANCEMENT d'un chapitre : ce que le déplacement des blocs lui a fait
 * gagner ou perdre, séparé de ce que le chiffrage a changé.
 *
 * *Demande de Chris, 24/09/2026 : « je voudrais ne pas mélanger dans la même colonne les
 * variations dues à des changements de prix, d'heures ou de quantité avec celles qui
 * proviennent du déplacement des chapitres » — et « le total général des variations
 * d'ordonnancement donnera 0 ».*
 *
 * ## La règle
 *
 * Le Δ d'un chapitre compare son montant à celui du chapitre de MÊME NUMÉRO dans le devis
 * figé. Depuis le déplacement des blocs (23/09/2026), le contenu des deux n'est plus le
 * même, et ce Δ mêle deux choses. On les sépare en valorisant chaque tâche **au prix du
 * devis figé** :
 *
 *   structure(N) = Σ devis des tâches sous N aujourd'hui − Σ devis des tâches sous N au devis
 *   Δ(N)         = Δ d'avant − structure(N)          (le chiffrage seul)
 *
 * La première ne dépend que de la STRUCTURE : aucun prix, aucune quantité, aucune case
 * cochée dans la copie de travail n'y entre.
 *
 * ## Le premier niveau, et le zéro
 *
 * *Tranché par Chris le 24/09/2026.* Un chapitre de premier niveau rangé dans un autre (5
 * DIVERS sous 3 sur APO260098) apporte tout son contenu à son nouveau parent, sans qu'aucune
 * ligne de premier niveau ne le perde. Il laisse donc au premier niveau une **ligne de
 * rappel**, qui porte l'opposé de son montant au devis. Symétriquement, un bloc remonté au
 * premier niveau y affiche tout ce qu'il apporte, puisque ce niveau ne le contenait pas.
 * Avec ces deux règles, **la colonne fait zéro au premier niveau par construction** : chaque
 * tâche y est comptée une fois de chaque côté. Le total est calculé, pas supposé, et s'affiche.
 *
 * ## Ce que vaut une tâche « au devis »
 *
 * Sa contribution EFFECTIVE au devis figé : son montant s'il y est retenu, lui et tous ses
 * parents ; zéro sinon. Déplacer une option écartée au devis ne déplace donc aucun euro. Une
 * tâche créée dans la contre-étude n'existe pas au devis : elle vaut zéro, et tout son
 * montant se lit dans le chiffrage — la règle du 21/08/2026.
 *
 * Les décimales passent par `dec` : ce sont des montants, et la somme de cent tâches en
 * virgule flottante ne retombe pas toujours sur zéro.
 */
import { dec } from "@planif/moteur";
import type { NoeudVue } from "@planif/moteur";

type Decimal = ReturnType<typeof dec>;

/** Un poste de premier niveau du devis qui n'y est plus : sa contrepartie s'affiche là. */
export type RappelDePremierNiveau = {
  readonly numero: string;
  readonly designation: string;
  /** Le chapitre sous lequel il est rangé aujourd'hui. */
  readonly sousLe: string | null;
  readonly variation: number;
};

export type Ordonnancement = {
  /** La colonne « Var. ordonnancement », par numéro : chapitres, et tâches de premier niveau. */
  readonly affichee: ReadonlyMap<string, number>;
  /** La part du Δ d'avant qui vient de la structure — c'est elle que le Δ ne porte plus. */
  readonly structure: ReadonlyMap<string, number>;
  readonly rappels: readonly RappelDePremierNiveau[];
  /** La somme du premier niveau, rappels compris. Zéro, et affichée pour qu'on le voie. */
  readonly total: number;
};

/** La contribution effective de chaque tâche au devis figé, par numéro. */
function contributionsAuDevis(racines: readonly NoeudVue[]): Map<string, Decimal> {
  const contributions = new Map<string, Decimal>();
  const descendre = (noeuds: readonly NoeudVue[], cheminRetenu: boolean): void => {
    for (const noeud of noeuds) {
      const retenu = cheminRetenu && noeud.retenu;
      if (noeud.nature === "chapitre") {
        descendre(noeud.enfants, retenu);
      } else {
        contributions.set(noeud.numero, retenu ? dec(noeud.montantRecalcule) : dec(0));
      }
    }
  };
  descendre(racines, true);
  return contributions;
}

/** Pour chaque poste de l'arbre, la somme des contributions des tâches qu'il contient. */
function sommesParPoste(
  racines: readonly NoeudVue[],
  contributions: ReadonlyMap<string, Decimal>,
): Map<string, Decimal> {
  const sommes = new Map<string, Decimal>();
  const sommer = (noeud: NoeudVue): Decimal => {
    const somme =
      noeud.nature === "ouvrage"
        ? (contributions.get(noeud.numero) ?? dec(0))
        : noeud.enfants.reduce((total, enfant) => total.plus(sommer(enfant)), dec(0));
    sommes.set(noeud.numero, somme);
    return somme;
  };
  for (const racine of racines) sommer(racine);
  return sommes;
}

/** Le parent de chaque poste de l'arbre, par numéro. */
function parents(racines: readonly NoeudVue[]): Map<string, string | null> {
  const index = new Map<string, string | null>();
  const descendre = (noeuds: readonly NoeudVue[], parent: string | null): void => {
    for (const noeud of noeuds) {
      index.set(noeud.numero, parent);
      if (noeud.nature === "chapitre") descendre(noeud.enfants, noeud.numero);
    }
  };
  descendre(racines, null);
  return index;
}

export function variationsDOrdonnancement(
  courant: readonly NoeudVue[],
  reference: readonly NoeudVue[],
): Ordonnancement {
  const contributions = contributionsAuDevis(reference);
  const aujourdHui = sommesParPoste(courant, contributions);
  const auDevis = sommesParPoste(reference, contributions);
  const racinesDuDevis = new Set(reference.map((n) => n.numero));
  const racinesCourantes = new Set(courant.map((n) => n.numero));

  const structure = new Map<string, number>();
  const affichee = new Map<string, number>();
  let total = dec(0);

  const parcourir = (noeuds: readonly NoeudVue[], premierNiveau: boolean): void => {
    for (const noeud of noeuds) {
      const somme = aujourdHui.get(noeud.numero) ?? dec(0);
      const ecart = somme.minus(auDevis.get(noeud.numero) ?? dec(0));
      if (noeud.nature === "chapitre") structure.set(noeud.numero, ecart.toNumber());
      if (premierNiveau) {
        // Au premier niveau, la référence est ce que CE NIVEAU contenait : rien, pour un
        // bloc qui n'y était pas au devis.
        const affiche = racinesDuDevis.has(noeud.numero) ? ecart : somme;
        affichee.set(noeud.numero, affiche.toNumber());
        total = total.plus(affiche);
      } else if (noeud.nature === "chapitre") {
        affichee.set(noeud.numero, ecart.toNumber());
      }
      if (noeud.nature === "chapitre") parcourir(noeud.enfants, false);
    }
  };
  parcourir(courant, true);

  const parentCourant = parents(courant);
  const rappels: RappelDePremierNiveau[] = [];
  for (const racine of reference) {
    if (racinesCourantes.has(racine.numero)) continue;
    const variation = dec(0).minus(auDevis.get(racine.numero) ?? dec(0));
    total = total.plus(variation);
    rappels.push({
      numero: racine.numero,
      designation: racine.designation,
      sousLe: parentCourant.get(racine.numero) ?? null,
      variation: variation.toNumber(),
    });
  }

  return { affichee, structure, rappels, total: total.toNumber() };
}
